import { BackButton, Button } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import { useKlaviyoCRUDActions } from '@mm/state/actions/KlaviyoCRUDActions';
import { useSignalConfigurationCRUDActions } from '@mm/state/actions/SignalConfigurationCRUDActions';
import {
  selectSignalConfiguration,
  selectSignalEvents,
} from '@mm/state/selectors/signalSelectors';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { Alert, Divider } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { TABLET_SMALL } from '../../../../../constants/screenSize';
import { useConnectKlaviyo } from '../../Integrations/NativeIntegrations/components/KlaviyoIntegrations/use-connect-klaviyo';
import { KlaviyoSendingSwitch } from '../KlaviyoSendingSwitch/KlaviyoSendingSwitch';
import { ManualSignal } from '../ManualSignal/ManualSignal';
import { ShopifySignal } from '../ShopifySignal/ShopifySignal';
import { SignalHeaderText } from '../Signal';
import { signalRangeSelectOptions } from '../SignalGraph/SignalGraph';
import styles from './SignalSettings.module.css';

export const SignalSettings = () => {
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= TABLET_SMALL;
  const history = useHistory();

  const chatbotId = useSelector(activeChatbotIdSelector);

  const configuration = useSelector(selectSignalConfiguration);
  const events = useSelector(selectSignalEvents);

  const [isShopifySignalActive, setIsShopifySignalActive] = useState(
    configuration?.mode === 'integrations',
  );
  const [isManualSignalActive, setIsManualSignalActive] = useState(
    configuration?.mode === 'manual',
  );

  const [isInitialStateSet, setIsInitialStateSet] = useState(false);

  const { fetch: fetchConfiguration, update: updateConfiguration } =
    useSignalConfigurationCRUDActions();

  const activeFbPageId = useSelector(activeFacebookPageIdSelector);

  const { getKlaviyoAccounts, getKlaviyoConnections, klaviyoConnections } =
    useConnectKlaviyo();
  const { invalidateStore: invalidateKlaviyoStore } = useKlaviyoCRUDActions();

  const { getEvents } = useSignalConfigurationCRUDActions();

  const [accountsFetched, setAccountsFetched] = useState(false);

  const klaviyoConnection = klaviyoConnections?.[0];

  const isKlaviyoConnected = accountsFetched && !!klaviyoConnection;

  useEffect(() => {
    (async () => {
      await getKlaviyoAccounts();
      setAccountsFetched(true);
    })();
    return () => {
      invalidateKlaviyoStore();
    };
  }, []);

  useEffect(() => {
    if (accountsFetched) {
      getKlaviyoConnections();
    }
  }, [accountsFetched]);

  useEffect(() => {
    if (activeFbPageId && !configuration) {
      getEvents({
        facebookPageId: activeFbPageId,
        range: signalRangeSelectOptions[0].value,
      });
      fetchConfiguration(activeFbPageId);
      if (isInitialStateSet) {
        setIsInitialStateSet(false);
      }
    }
  }, [activeFbPageId]);

  useEffect(() => {
    if (configuration) {
      if (configuration?.mode === 'integrations' && !isShopifySignalActive) {
        setIsShopifySignalActive(true);
      } else if (configuration?.mode === 'manual' && !isManualSignalActive) {
        setIsManualSignalActive(true);
      }
      setIsInitialStateSet(true);
    }
  }, [configuration]);

  useEffect(() => {
    if (configuration?.id && isInitialStateSet) {
      let newMode = configuration.mode;

      if (!isShopifySignalActive && !isManualSignalActive) {
        newMode = 'off';
      } else if (isShopifySignalActive) {
        newMode = 'integrations';
      } else if (isManualSignalActive) {
        newMode = 'manual';
      }

      if (newMode !== configuration.mode) {
        updateConfiguration({
          id: configuration.id,
          mode: newMode,
        });
      }
    }
  }, [isShopifySignalActive, isManualSignalActive, configuration?.id]);

  useEffect(() => {
    if (!configuration && activeFbPageId) {
      fetchConfiguration(activeFbPageId);
    }
    if (!events && activeFbPageId) {
      getEvents({
        facebookPageId: activeFbPageId,
        range: signalRangeSelectOptions[0].value,
      });
    }
  }, [configuration, events, activeFbPageId]);

  const handleShopifySignalToggle = (
    checked: boolean,
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    setIsShopifySignalActive(checked);
    if (isManualSignalActive && checked) {
      setIsManualSignalActive(false);
    }
  };

  const handleManualSignalToggle = (
    checked: boolean,
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    setIsManualSignalActive(checked);
    if (isShopifySignalActive && checked) {
      setIsShopifySignalActive(false);
    }
  };

  const handleBackClick = () => {
    history.push(BotBuilderRoutes.signal.getLink({ chatbotId }));
  };

  const handleConnectKlaviyo = () => {
    history.push(BotBuilderRoutes.integrations.getLink({ chatbotId }), {
      kind: 'native-integrations',
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <div className={styles.backButtonContainer}>
          <BackButton onClick={handleBackClick} text="" />
        </div>
        {!isMobile && <SignalHeaderText />}
      </div>
      {!isKlaviyoConnected && (
        <Alert
          className={styles.alert}
          message="Klaviyo is not connected"
          description={
            <Button
              onClick={handleConnectKlaviyo}
              css={{ container: styles.button }}
            >
              Connect Klaviyo
            </Button>
          }
          type="warning"
          showIcon
        />
      )}
      <div className={styles.sectionContainer}>
        <div className={styles.sectionTitle}>
          Step 1. Choose a way to gather data signal events:
        </div>
        <ShopifySignal
          data={events?.klaviyoShopify || {}}
          isShopifySignalActive={isShopifySignalActive}
          handleShopifySignalToggle={handleShopifySignalToggle}
        />
        <ManualSignal
          isManualSignalActive={isManualSignalActive}
          handleManualSignalToggle={handleManualSignalToggle}
        />
      </div>
      <div className={styles.sectionContainer}>
        <div className={styles.sectionTitle}>
          Step 2. Configure where to send data signal events to:
        </div>

        <KlaviyoSendingSwitch />
        <Divider />
      </div>
    </div>
  );
};
